import { useApiError } from "@raiden/library/components/ReactHookForm/ApiError";
import useRequest from "@raiden/library/hooks/useRequest";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

/**
 * @typedef {object} WizardInformationFormValues
 * @property {object} data
 * @property {import("@raiden/library/types/Customer").CustomerGender | ""} data.gender
 * @property {string} data.last_name
 * @property {string} data.first_name
 * @property {string} data.birthdate
 * @property {string} data.address_1
 * @property {string} data.address_2
 * @property {string} data.zipcode
 * @property {string} data.city
 * @property {string} data.country_id
 * @property {string} data.phone_country
 * @property {string} data.phone_number
 */

/**
 * @typedef {object} Params
 * @property {import("@raiden/library/types/Customer").Customer} customer
 *
 * @param {Params} params
 * @returns {WizardInformationFormValues}
 */
function getDefaultValues({ customer }) {
  return {
    data: {
      // customer fields
      gender: customer.gender ?? "",
      last_name: customer.last_name ?? "",
      first_name: customer.first_name ?? "",
      birthdate: customer.birthdate ?? "",
      // address fields
      address_1: customer.default_address?.address_1 ?? "",
      address_2: customer.default_address?.address_2 ?? "",
      zipcode: customer.default_address?.zipcode ?? "",
      city: customer.default_address?.city ?? "",
      country_id: customer.default_address?.country.id
        ? String(customer.default_address?.country.id)
        : "",
      phone_country: customer.default_address?.phone_country ?? "",
      phone_number: customer.default_address?.phone_number ?? "",
    },
  };
}

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Customer").Customer} customer
 * @property {() => void} onSuccess
 * @property {(params: {
 * isLoading: boolean,
 * form: import("react-hook-form").UseFormReturn<any>,
 * }) => import("react").ReactElement} render
 *
 * @param {Props} props
 */
function Handler({ customer, onSuccess, render }) {
  const intl = useIntl();

  const { resolveApiError, resetApiErrors } = useApiError();

  const form = useForm({ defaultValues: getDefaultValues({ customer }) });
  const { handleSubmit } = form;

  const { request: requestCustomer, isLoading: isLoadingCustomer } =
    useRequest();

  const {
    request: requestAddress,
    isLoading: isLoadingAddress,
    toastSuccess,
  } = useRequest();

  /**
   * @param {WizardInformationFormValues} values
   */
  function onSubmit(values) {
    requestCustomer(
      generateApiUri({
        id: "@customers.update",
        parameters: { customerId: customer.id },
      }),
      {
        method: "POST",
        body: {
          data: {
            gender: values.data.gender,
            last_name: values.data.last_name,
            first_name: values.data.first_name,
            birthdate: values.data.birthdate,
          },
        },
      },
    )
      .then((response) => {
        resetApiErrors();
        requestAddress(
          generateApiUri({
            id: "@addresses.update",
            parameters: { addressId: customer.default_address?.id },
          }),
          {
            method: "POST",
            body: {
              data: {
                address_1: values.data.address_1,
                address_2: values.data.address_2,
                zipcode: values.data.zipcode,
                city: values.data.city,
                country_id: values.data.country_id,
                phone_country: values.data.phone_country,
                phone_number: values.data.phone_number,
              },
            },
          },
        )
          .then(() => {
            resetApiErrors();
            onSuccess();
            toastSuccess({
              title: intl.formatMessage({
                defaultMessage:
                  "Les modifications ont bien été prises en compte.",
              }),
            });
          })
          .catch((response) => {
            resolveApiError({ form, response });
          });
      })
      .catch((response) => {
        resolveApiError({ form, response });
      });
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {render({
          isLoading: isLoadingCustomer || isLoadingAddress,
          form,
        })}
      </form>
    </FormProvider>
  );
}

export default Handler;
